/* eslint-disable */
// src/components/FolderMirroring.tsx
//
// Folder Mirroring: drop a local folder, auto-detect new folders/files
// vs SharePoint, create missing folders, upload new files only.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchSharePointTree, flattenTreeByPath, type SPTreeNode } from "../utils/sharePointTree";
import {
  computeMirrorDiff, scanLocalTree, expandZipForMirror, captureDropEntries, materializeFile,
  type LocalScan, type DroppedEntries,
} from "../utils/folderMirror";
import { createFolderInSharePoint, fileExistsInFolder, uploadFileToParentItem } from "../utils/sharepointupload";
import { sendUploadSummaryEmail } from "../utils/uploadAndEmail";
import { getExcludedFolders, saveExcludedFolders } from "../store/folderStore";
import ExcludeFolderTree, { treeFromEntries, treeFromPaths } from "./ExcludeFolderTree";
import { useIrlMatches } from "../irl/match/useIrlMatches";
import { isCodeExcluded, pathToCode } from "../irl/match/folderTree";
import {
  buildFolderCatalog,
  estimateFolderMatchCost,
  formatCostEstimate,
  runFolderMatch,
  type FolderMatch,
} from "../irl/match/folderMatchClient";
import type { IRLItem } from "../irl/match/types";
import { getIRLMatchExcludedFolders, getIRLRequests } from "../irl/store/irlStore";
import { getIndexRows } from "../store/folderStore";

type Props = {
  driveId: string;
  rootItemId: string;
  rootFolderName: string;
  projectName?: string;
  /** Also enables IRL matching against the project's saved IRL list. */
  projectId?: string;   // used to remember excluded folders per project
  notifyEmail?: string; // optional email for summary
};

type UploadStatus = {
  fileName: string;
  folderPath: string;
  status: "queued" | "uploading" | "done" | "skipped" | "error";
  progress?: number;
  error?: string;
  note?: string;
  webUrl?: string;
};

export default function FolderMirroring({ driveId, rootItemId, rootFolderName, projectName, projectId, notifyEmail }: Props) {
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const zipInputRef = useRef<HTMLInputElement | null>(null);

  const [spTree, setSpTree] = useState<SPTreeNode | null>(null);
  const [fetchingTree, setFetchingTree] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(0);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [localScan, setLocalScan] = useState<LocalScan | null>(null);
  const [comparing, setComparing] = useState(false);
  const [scanProgress, setScanProgress] = useState<{ files: number; folders: number } | null>(null);

  // A dragged folder waits here (entries only, nothing walked) until the user
  // has picked exclusions and hits Compare. ZIPs and the input picker skip this.
  const [pendingDrop, setPendingDrop] = useState<DroppedEntries | null>(null);

  // Excluded folders (lowercase relative paths) — remembered per project
  const [excluded, setExcluded] = useState<Set<string>>(
    () => new Set(projectId ? getExcludedFolders(projectId) : [])
  );
  const [showExclude, setShowExclude] = useState(false);
  useEffect(() => {
    setExcluded(new Set(projectId ? getExcludedFolders(projectId) : []));
  }, [projectId]);

  // Diff is derived, so toggling an exclusion (or re-scanning SP) updates it instantly
  const diff = useMemo(
    () => (localScan && spTree ? computeMirrorDiff(localScan, spTree, excluded) : null),
    [localScan, spTree, excluded]
  );

  const spFolderMap = useMemo(() => (spTree ? flattenTreeByPath(spTree) : null), [spTree]);

  // Tree shown in the exclude panel: lazy from the drop, eager from a finished scan
  const treeRoots = useMemo(
    () => (pendingDrop ? treeFromEntries(pendingDrop.roots) : localScan ? treeFromPaths(localScan.folderPaths) : []),
    [pendingDrop, localScan]
  );

  // With a lazy tree we can't know which saved exclusions match, so show them all
  const activeExcludedCount = useMemo(
    () => (pendingDrop ? excluded.size : localScan ? localScan.folderPaths.filter((p) => excluded.has(p.toLowerCase())).length : 0),
    [pendingDrop, localScan, excluded]
  );

  const persistExcluded = (next: Set<string>) => {
    setExcluded(next);
    if (projectId) saveExcludedFolders(projectId, Array.from(next));
    // Excluded folders were never walked, so a finished scan is stale — ask for Compare again
    if (pendingDrop && localScan) setLocalScan(null);
  };

  const toggleExcluded = (path: string) => {
    const key = path.toLowerCase();
    const next = new Set(excluded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    persistExcluded(next);
  };

  const clearExcluded = () => persistExcluded(new Set());

  const [uploading, setUploading] = useState(false);
  const [statuses, setStatuses] = useState<UploadStatus[]>([]);
  const [messages, setMessages] = useState<string[]>([]);
  const [folderStatuses, setFolderStatuses] = useState<{ path: string; status: "queued" | "creating" | "done" | "error"; error?: string }[]>([]);

  // ── IRL matching for the new-files list ───────────────────────────────────
  // We re-derive everything per render from `diff.newFiles`. The hook handles
  // the network/cache; we just supply files + folder codes (resolved from each
  // mirror file's local folderPath via the saved index titles).
  const newMirrorFiles = useMemo(() => diff?.newFiles ?? [], [diff]);
  const irlFiles = useMemo<File[]>(() => newMirrorFiles.map((mf) => mf.file), [newMirrorFiles]);
  const irlFolderCodes = useMemo<(string | null)[]>(() => {
    if (!projectId) return newMirrorFiles.map(() => null);
    const indexRows = getIndexRows(projectId) || [];
    return newMirrorFiles.map((mf) => pathToCode(mf.folderPath, indexRows));
  }, [projectId, newMirrorFiles]);
  const irl = useIrlMatches({ projectId, files: irlFiles, folderCodes: irlFolderCodes });
  const irlListMap = useMemo(() => {
    if (!projectId) return new Map<string, { topic?: string; request: string }>();
    const list = getIRLRequests(projectId);
    return new Map(list.map((r) => [r.number, { topic: r.topic, request: r.request }]));
  }, [projectId, irl.matches]);
  const excludedFoldersSet = useMemo(
    () => (projectId ? new Set(getIRLMatchExcludedFolders(projectId)) : new Set<string>()),
    [projectId],
  );
  const irlMatchedCount = Object.values(irl.matches).filter((m) => !!m.irlNumber).length;

  // ── Folder match (folder name → IRL item) ─────────────────────────────────
  // Sister of the per-file matcher. The catalog is grouped by folder code,
  // honors the same excluded-folders selection, and ships ~5 sample file names
  // per folder to give Claude secondary signal beyond the folder name itself.
  const irlListForMatch = useMemo<IRLItem[]>(() => {
    if (!projectId) return [];
    return getIRLRequests(projectId).map((r) => ({
      number: r.number,
      topic: r.topic,
      entityName: r.entityName,
      request: r.request,
    }));
  }, [projectId]);
  const folderCatalog = useMemo(() => {
    if (newMirrorFiles.length === 0) return [];
    return buildFolderCatalog({
      items: newMirrorFiles.map((mf, i) => ({
        folderPath: mf.folderPath,
        code: irlFolderCodes[i] ?? null,
        fileName: mf.file.name,
      })),
      excludedFolders: excludedFoldersSet,
    });
  }, [newMirrorFiles, irlFolderCodes, excludedFoldersSet]);
  const folderMatchCost = useMemo(
    () => estimateFolderMatchCost({ irlList: irlListForMatch, folders: folderCatalog }),
    [irlListForMatch, folderCatalog],
  );

  const [folderMatchResults, setFolderMatchResults] =
    useState<Record<string, FolderMatch[]>>({});
  const [folderMatching, setFolderMatching] = useState(false);
  const [folderMatchError, setFolderMatchError] = useState<string | null>(null);
  const [showFindDocsConfirm, setShowFindDocsConfirm] = useState(false);

  const folderMatchHitCount = useMemo(
    () => Object.values(folderMatchResults).filter((arr) => arr.length > 0).length,
    [folderMatchResults],
  );

  const handleFindDocuments = async () => {
    setShowFindDocsConfirm(false);
    setFolderMatchError(null);
    if (folderCatalog.length === 0 || irlListForMatch.length === 0) return;
    setFolderMatching(true);
    try {
      const matches = await runFolderMatch({
        irlList: irlListForMatch,
        folders: folderCatalog,
      });
      setFolderMatchResults(matches);
    } catch (err: any) {
      setFolderMatchError(err?.message || "Folder match failed");
    } finally {
      setFolderMatching(false);
    }
  };

  const addMsg = (msg: string) =>
    setMessages((prev) => [...prev, msg]);

  // ----- Fetch SharePoint (with files) -----
  const handleFetchTree = async () => {
    setFetchingTree(true);
    setFetchProgress(0);
    addMsg("🔄 Scanning SharePoint folders and files...");

    try {
      const tree = await fetchSharePointTree(
        driveId,
        rootItemId,
        (count) => setFetchProgress(count),
        true // includeFiles
      );
      setSpTree(tree);
      setFetchedAt(new Date().toISOString());

      let folderCount = 0;
      let fileCount = 0;
      const walk = (n: SPTreeNode) => {
        folderCount += n.children.length;
        fileCount += n.files?.length || 0;
        n.children.forEach(walk);
      };
      walk(tree);
      addMsg(`✅ Found ${folderCount} folders and ${fileCount} files in SharePoint.`);
    } catch (err: any) {
      addMsg(`❌ Scan failed: ${err?.message || String(err)}`);
    } finally {
      setFetchingTree(false);
    }
  };

  // ----- Run comparison from a LocalScan -----
  const runComparison = async (scan: LocalScan, label: string) => {
    setDroppedFiles(scan.files);

    if (scan.files.length === 0 && scan.folderPaths.length === 0) {
      addMsg(`⚠️ No files or folders detected in ${label}. Try again.`);
      return;
    }

    addMsg(`📂 Found ${scan.files.length} file(s) and ${scan.folderPaths.length} folder(s) in ${label}.`);

    setLocalScan(scan);
    const d = computeMirrorDiff(scan, spTree!, excluded);
    addMsg(
      `📊 ${d.newFolders.length} new folder(s), ${d.newFiles.length} new file(s), ${d.skippedFiles.length} already in SharePoint` +
        (d.excludedFileCount > 0 ? `, ${d.excludedFileCount} in excluded folder(s).` : ".")
    );
  };

  // ----- Dragged folder: capture entries only, wait for the user to Compare -----
  const handleFolderDrop = async (items: DataTransferItemList) => {
    if (!spTree) {
      addMsg("⚠️ Scan SharePoint first so we know what's already there.");
      return;
    }
    // captureDropEntries snapshots the entries synchronously — must run before any await
    const captured = captureDropEntries(items);
    try {
      const dropped = await captured;
      if (dropped.roots.length === 0) {
        addMsg("⚠️ Nothing detected in the dropped folder. Try again.");
        return;
      }
      setPendingDrop(dropped);
      setLocalScan(null);
      setDroppedFiles([]);
      setShowExclude(true);
      addMsg("📂 Folder loaded. Tick any folders to exclude, then click Compare with SharePoint.");
    } catch (err: any) {
      addMsg(`❌ Failed: ${err?.message || String(err)}`);
    }
  };

  // ----- Compare: walk the dropped folder (skipping exclusions), then diff -----
  const runCompare = async () => {
    if (!pendingDrop || !spTree) return;
    setComparing(true);
    setScanProgress({ files: 0, folders: 0 });
    try {
      addMsg("📂 Scanning local folder...");
      const scan = await scanLocalTree(pendingDrop, excluded, (files, folders) =>
        setScanProgress({ files, folders })
      );
      await runComparison(scan, "local folder");
    } catch (err: any) {
      addMsg(`❌ Failed: ${err?.message || String(err)}`);
    } finally {
      setComparing(false);
      setScanProgress(null);
    }
  };

  // ----- ZIP drop or input-picker FileList: everything is already enumerated, go straight to diff -----
  const handleLocalSource = async (source: FileList) => {
    if (!spTree) {
      addMsg("⚠️ Scan SharePoint first so we know what's already there.");
      return;
    }

    // Check if a ZIP was dropped
    const firstFile = source[0] as File | undefined;
    const isZipDrop = firstFile && (/\.zip$/i.test(firstFile.name) || firstFile.type === "application/zip");

    setComparing(true);
    setPendingDrop(null);

    try {
      if (isZipDrop) {
        addMsg(`📦 Extracting ZIP: ${firstFile.name}...`);
        const scan = await expandZipForMirror(firstFile);
        await runComparison(scan, "ZIP");
      } else {
        addMsg("📂 Scanning local folder...");
        const scan = await scanLocalTree(source, excluded);
        await runComparison(scan, "local folder");
      }
    } catch (err: any) {
      addMsg(`❌ Failed: ${err?.message || String(err)}`);
    } finally {
      setComparing(false);
    }
  };

  // ----- ZIP file picker -----
  const handleZipPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!spTree) {
      addMsg("⚠️ Scan SharePoint first so we know what's already there.");
      return;
    }

    setComparing(true);
    setPendingDrop(null);
    try {
      addMsg(`📦 Extracting ZIP: ${file.name}...`);
      const scan = await expandZipForMirror(file);
      await runComparison(scan, "ZIP");
    } catch (err: any) {
      addMsg(`❌ Failed: ${err?.message || String(err)}`);
    } finally {
      setComparing(false);
      if (zipInputRef.current) zipInputRef.current.value = "";
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const items = e.dataTransfer.items;
    // Check if a single ZIP file was dropped
    if (e.dataTransfer.files.length === 1) {
      const f = e.dataTransfer.files[0];
      if (/\.zip$/i.test(f.name) || f.type === "application/zip") {
        await handleLocalSource(e.dataTransfer.files);
        return;
      }
    }
    if (items && items.length && (items[0] as any)?.webkitGetAsEntry) {
      await handleFolderDrop(items);
    } else if (e.dataTransfer.files.length) {
      await handleLocalSource(e.dataTransfer.files);
    }
  };

  const onInputPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length) handleLocalSource(files);
  };

  // ----- Upload: create folders, then upload files -----
  const runUpload = async () => {
    if (!diff || !spTree) return;

    setUploading(true);

    const workingMap = new Map(diff.existingFolderMap);

    // 1. Create missing folders (sorted by depth)
    const foldersByDepth = [...diff.newFolders].sort(
      (a, b) => a.split("/").length - b.split("/").length
    );

    // Initialize folder statuses for visible progress
    setFolderStatuses(foldersByDepth.map((p) => ({ path: p, status: "queued" as const })));

    let createdFolders = 0;
    let failedFolders = 0;

    for (let idx = 0; idx < foldersByDepth.length; idx++) {
      const folderPath = foldersByDepth[idx];
      const segments = folderPath.split("/");
      const parentPath = segments.slice(0, -1).join("/");
      const folderName = segments[segments.length - 1];
      const parentId = parentPath ? workingMap.get(parentPath.toLowerCase()) : rootItemId;

      if (!parentId) {
        failedFolders++;
        setFolderStatuses((prev) => prev.map((s, i) => (i === idx ? { ...s, status: "error", error: "Parent folder missing" } : s)));
        addMsg(`❌ Cannot create "${folderPath}" — parent missing.`);
        continue;
      }

      setFolderStatuses((prev) => prev.map((s, i) => (i === idx ? { ...s, status: "creating" } : s)));

      try {
        console.log(`📁 Creating folder "${folderPath}" under parent=${parentId}`);
        const newId = await createFolderInSharePoint(driveId, parentId, folderName);
        workingMap.set(folderPath, newId);
        workingMap.set(folderPath.toLowerCase(), newId);
        setFolderStatuses((prev) => prev.map((s, i) => (i === idx ? { ...s, status: "done" } : s)));
        createdFolders++;
      } catch (err: any) {
        failedFolders++;
        const errMsg = err?.message || String(err);
        console.error(`❌ Folder creation failed for "${folderPath}":`, err);
        setFolderStatuses((prev) => prev.map((s, i) => (i === idx ? { ...s, status: "error", error: errMsg } : s)));
        addMsg(`❌ "${folderPath}": ${errMsg}`);
      }
    }

    if (createdFolders > 0) addMsg(`✅ Created ${createdFolders} folder(s).`);
    if (failedFolders > 0) addMsg(`⚠️ Failed to create ${failedFolders} folder(s).`);

    // 2. A file that sits in several folders goes to every one of them. The scan may
    // predate a copy being deleted, so check each "already synced" copy live and
    // upload the ones that are actually missing.
    const toUpload = [...diff.newFiles];
    const recheck = diff.skippedFiles.filter((f) => (diff.copyCounts.get(f.fileName.toLowerCase()) ?? 0) > 1);
    if (recheck.length > 0) {
      addMsg(`🔍 Checking ${recheck.length} copies of files that belong in more than one folder…`);
      for (const mf of recheck) {
        const parentId = mf.folderPath ? workingMap.get(mf.folderPath.toLowerCase()) : rootItemId;
        if (parentId && !(await fileExistsInFolder(driveId, parentId, mf.fileName))) toUpload.push(mf);
      }
      const missing = toUpload.length - diff.newFiles.length;
      addMsg(missing > 0
        ? `↩️ ${missing} of them were missing from their folder — uploading those too.`
        : "✅ All of them are already in their folders.");
    }

    // 3. Initialize file statuses
    const initial: UploadStatus[] = toUpload.map((f) => ({
      fileName: f.fileName,
      folderPath: f.folderPath,
      status: "queued",
    }));
    setStatuses(initial);

    // 4. Upload files
    let done = 0;
    let errored = 0;
    let skippedByUpload = 0;
    const successfulUploads: { name: string; url: string; folderPath: string }[] = [];

    for (let i = 0; i < toUpload.length; i++) {
      const mf = toUpload[i];
      const parentId = mf.folderPath
        ? workingMap.get(mf.folderPath.toLowerCase())
        : rootItemId;

      if (!parentId) {
        errored++;
        setStatuses((prev) =>
          prev.map((s, idx) =>
            idx === i ? { ...s, status: "error", error: "Parent folder missing" } : s
          )
        );
        continue;
      }

      setStatuses((prev) =>
        prev.map((s, idx) => (idx === i ? { ...s, status: "uploading" } : s))
      );

      try {
        // Pull the bytes into memory first — files streamed from Box Drive are
        // placeholders and fail mid-upload with an opaque "Network Error".
        const fileToUpload = await materializeFile(mf.file);

        // renameFolders=false: the mirror finds folders by name, so a too-long path
        // shortens the file name instead of renaming SharePoint folders (which made
        // the next run re-create them as duplicates).
        const result = await uploadFileToParentItem(driveId, parentId, fileToUpload, (frac) => {
          setStatuses((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, progress: Math.round(frac * 100) } : s
            )
          );
        }, undefined, undefined, false);

        // Upload function returns { skipped: true, reason, message } if it detects
        // a duplicate in SharePoint — don't count those as successful uploads.
        if (result?.skipped) {
          skippedByUpload++;
          setStatuses((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: "skipped", note: result?.reason || "duplicate" } : s
            )
          );
          continue;
        }

        done++;
        const webUrl = result?.webUrl || result?.data?.webUrl || "";
        setStatuses((prev) =>
          prev.map((s, idx) =>
            idx === i ? { ...s, status: "done", progress: 100, webUrl } : s
          )
        );
        successfulUploads.push({
          name: mf.fileName,
          url: webUrl,
          folderPath: mf.folderPath || "root",
        });
      } catch (err: any) {
        errored++;
        // Axios reports an unreadable file body as a bare "Network Error" — say what it means.
        const raw = err?.message || String(err);
        const errMsg = /network error/i.test(raw)
          ? "Could not read file from disk (cloud drive may not have downloaded it)"
          : raw;
        console.error(`❌ Upload failed for "${mf.relativePath}":`, err);
        setStatuses((prev) =>
          prev.map((s, idx) => (idx === i ? { ...s, status: "error", error: errMsg } : s))
        );
      }
    }

    addMsg(`✅ Upload complete. ${done} succeeded, ${skippedByUpload} skipped by SharePoint, ${errored} failed.`);

    // 5. Send email summary (same shape as dataroom upload email)
    if (successfulUploads.length > 0) {
      try {
        const grouped: Record<string, { name: string; url: string }[]> = {};
        for (const u of successfulUploads) {
          if (!grouped[u.folderPath]) grouped[u.folderPath] = [];
          grouped[u.folderPath].push({ name: u.name, url: u.url });
        }
        await sendUploadSummaryEmail({
          grouped,
          folderLink: rootFolderName,
          title: projectName ? `New Uploads – ${projectName}` : `Folder Mirroring: ${done} files uploaded`,
        });
        addMsg("📧 Summary email sent.");
      } catch (err: any) {
        addMsg(`⚠️ Email failed: ${err?.message || String(err)}`);
      }
    }

    setUploading(false);
  };

  // ----- Render -----
  const hasSPFetch = !!spTree;

  const multiFolderFiles = useMemo(() => {
    const counts = diff ? Array.from(diff.copyCounts.values()).filter((n) => n > 1) : [];
    // "Already synced" copies that get a live check at upload time
    const toCheck = diff
      ? diff.skippedFiles.filter((f) => (diff.copyCounts.get(f.fileName.toLowerCase()) ?? 0) > 1).length
      : 0;
    return { names: counts.length, copies: counts.reduce((a, b) => a + b, 0), toCheck };
  }, [diff]);
  const nothingToDo = !diff || (diff.newFiles.length === 0 && multiFolderFiles.toCheck === 0);

  return (
    <>
      {/* Step 1: Scan SharePoint */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={handleFetchTree}
            disabled={fetchingTree || uploading}
            style={{
              background: fetchingTree ? "#9bbf8f" : hasSPFetch ? "#6aa84f" : "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 14px",
              cursor: fetchingTree || uploading ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {fetchingTree
              ? `Scanning... ${fetchProgress} folders`
              : hasSPFetch
              ? `✓ SharePoint scanned (re-scan)`
              : "1️⃣ Scan SharePoint"}
          </button>
          <span style={{ fontSize: 12, color: "#4a7d4a" }}>
            {hasSPFetch
              ? `Fetched ${fetchedAt ? new Date(fetchedAt).toLocaleTimeString() : ""} — re-scan to refresh.`
              : "Click to scan the target SharePoint folder for existing folders and files."}
          </span>
        </div>
      </div>

      {/* Step 2: Drop local folder or upload ZIP */}
      {hasSPFetch && (
        <div style={{ marginTop: 16 }}>
          {/* Drop zone — accepts both folders and ZIPs */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            style={{
              border: "2px dashed #c1e2b3",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
              cursor: "pointer",
              background: droppedFiles.length > 0 || pendingDrop ? "#f4fbf1" : "#fff",
              transition: "all 0.15s",
            }}
            onClick={() => folderInputRef.current?.click()}
          >
            {comparing ? (
              <span style={{ color: "#4a7d4a" }}>
                {scanProgress
                  ? `Scanning local folder… ${scanProgress.folders} folders, ${scanProgress.files} files`
                  : "Comparing against SharePoint…"}
              </span>
            ) : pendingDrop && !localScan ? (
              <div>
                <div style={{ color: "#2f6b2f", fontWeight: 600 }}>📂 Folder loaded</div>
                <div style={{ fontSize: 11, color: "#4a7d4a", marginTop: 4 }}>
                  Tick folders to exclude below, then click Compare with SharePoint
                </div>
              </div>
            ) : droppedFiles.length > 0 ? (
              <div>
                <div style={{ color: "#2f6b2f", fontWeight: 600 }}>
                  📂 {droppedFiles.length} file{droppedFiles.length !== 1 ? "s" : ""} loaded
                </div>
                <div style={{ fontSize: 11, color: "#4a7d4a", marginTop: 4 }}>
                  Drop again to replace
                </div>
              </div>
            ) : (
              <div>
                <span style={{ color: "#4a7d4a" }}>
                  2️⃣ Drop a folder or ZIP here
                </span>
              </div>
            )}
          </div>

          {/* Picker buttons row */}
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button
              onClick={() => folderInputRef.current?.click()}
              disabled={comparing || uploading}
              style={{
                flex: 1,
                background: "#f4fbf1",
                color: "#2f6b2f",
                border: "1px solid #c1e2b3",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: comparing || uploading ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              📂 Browse Folder
            </button>
            <button
              onClick={() => zipInputRef.current?.click()}
              disabled={comparing || uploading}
              style={{
                flex: 1,
                background: "#f4fbf1",
                color: "#2f6b2f",
                border: "1px solid #c1e2b3",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: comparing || uploading ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              📦 Upload ZIP
            </button>
          </div>

          {/* Hidden inputs */}
          <input
            ref={folderInputRef}
            type="file"
            multiple
            // @ts-ignore (directory pick)
            webkitdirectory=""
            directory=""
            onChange={onInputPick}
            style={{ display: "none" }}
          />
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            onChange={handleZipPick}
            style={{ display: "none" }}
          />
        </div>
      )}

      {/* Exclude folders from the dropped tree */}
      {(pendingDrop || (localScan && localScan.folderPaths.length > 0)) && (
        <div
          style={{
            marginTop: 16,
            border: "1px solid #c1e2b3",
            borderRadius: 10,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#eaf7e6",
              padding: "10px 16px",
              borderBottom: showExclude ? "1px solid #c1e2b3" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <span style={{ fontWeight: 700, color: "#2f6b2f" }}>
                ⛔ Exclude folders
                {activeExcludedCount > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11, background: "#fee2e2", color: "#b91c1c", borderRadius: 10, padding: "1px 8px", fontWeight: 600 }}>
                    {activeExcludedCount} excluded
                  </span>
                )}
              </span>
              <div style={{ fontSize: 11, color: "#4a7d4a", marginTop: 2 }}>
                Tick a folder to leave it and everything inside it out of the mirror. Saved for this project.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {activeExcludedCount > 0 && (
                <button
                  onClick={clearExcluded}
                  disabled={uploading}
                  style={{
                    background: "#fff",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: uploading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setShowExclude((v) => !v)}
                style={{
                  background: "#f4fbf1",
                  color: "#2f6b2f",
                  border: "1px solid #c1e2b3",
                  borderRadius: 8,
                  padding: "6px 10px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {showExclude ? "Hide folders ▴" : "Choose folders ▾"}
              </button>
            </div>
          </div>
          {showExclude && (
            <div style={{ padding: "8px 10px", maxHeight: 320, overflow: "auto", background: "#fafdf8" }}>
              <ExcludeFolderTree
                roots={treeRoots}
                excluded={excluded}
                onToggle={toggleExcluded}
                isNewFolder={(p) => !!spFolderMap && !spFolderMap.has(p.toLowerCase())}
              />
            </div>
          )}
          {/* Dragged folder waiting for confirmation (or exclusions changed after a compare) */}
          {pendingDrop && !localScan && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #c1e2b3", background: "#fff" }}>
              <button
                onClick={runCompare}
                disabled={comparing || uploading}
                style={{
                  background: comparing || uploading ? "#9bbf8f" : "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 14px",
                  cursor: comparing || uploading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  width: "100%",
                }}
              >
                {comparing
                  ? scanProgress
                    ? `Scanning… ${scanProgress.folders} folders, ${scanProgress.files} files`
                    : "Comparing…"
                  : `Compare with SharePoint →`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Diff preview */}
      {diff && (
        <div
          style={{
            marginTop: 16,
            border: "1px solid #c1e2b3",
            borderRadius: 10,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#eaf7e6",
              padding: "12px 16px",
              borderBottom: "1px solid #c1e2b3",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontWeight: 700, color: "#2f6b2f" }}>
              Mirror Preview
            </div>
            <div style={{ fontSize: 12, color: "#4a7d4a" }}>
              Review counts, then confirm upload
            </div>
          </div>

          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <Stat label="New folders" value={diff.newFolders.length} color="#d68910" />
              <Stat label="New files" value={diff.newFiles.length} color="#6aa84f" />
              <Stat label="Already synced" value={diff.skippedFiles.length} color="#6b7280" />
              {diff.excludedFileCount > 0 && (
                <Stat label="Excluded" value={diff.excludedFileCount} color="#dc2626" />
              )}
            </div>

            {multiFolderFiles.names > 0 && (
              <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                📑 {multiFolderFiles.names} file{multiFolderFiles.names !== 1 ? "s" : ""} appear in more than one folder ({multiFolderFiles.copies} copies).
                Each copy goes to every folder it belongs in. Copies marked already synced are re-checked in SharePoint at upload, and only missing ones are uploaded.
              </div>
            )}

            {diff.newFolders.length > 0 && (
              <details open style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#92400e" }}>
                  New folders to create ({diff.newFolders.length})
                </summary>
                <div style={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#4a7d4a",
                  marginTop: 8,
                  maxHeight: 180,
                  overflow: "auto",
                  background: "#fafdf8",
                  padding: 8,
                  borderRadius: 6,
                }}>
                  {diff.newFolders.map((f) => (
                    <div key={f}>📁 {f}</div>
                  ))}
                </div>
              </details>
            )}

            {diff.newFiles.length > 0 && (
              <details open style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#2f6b2f" }}>
                  New files to upload ({diff.newFiles.length})
                </summary>

                {projectId && irl.hasIrlList && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: "#4a7d4a", flex: 1, minWidth: 220 }}>
                      Match files to IRL items. <strong>Find documents</strong> checks folder names and surfaces likely-matching folders to review.
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setShowFindDocsConfirm(true)}
                        disabled={folderMatching || folderCatalog.length === 0}
                        title={
                          folderCatalog.length === 0
                            ? "No folders to analyze — pick folders to include in the IRL tab first."
                            : `Match ${folderCatalog.length} folders to IRL items by name + sample files.`
                        }
                        style={{
                          background: folderMatching ? "#9ca3af" : "#fff",
                          color: folderMatching ? "#fff" : "#2f6b2f",
                          border: "1px solid #2f6b2f",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: folderMatching || folderCatalog.length === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        {folderMatching
                          ? "Finding…"
                          : folderMatchHitCount > 0
                            ? `Re-find documents (${folderMatchHitCount} hits)`
                            : "Find documents"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void irl.classify()}
                        disabled={irl.classifying || diff.newFiles.length === 0}
                        title="Use Claude to match each new file to an IRL request."
                        style={{
                          background: irl.classifying ? "#9ca3af" : "#2f6b2f",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: irl.classifying || diff.newFiles.length === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        {irl.classifying
                          ? "Matching IRL…"
                          : irlMatchedCount > 0
                            ? `Re-run IRL match (${irlMatchedCount} matched)`
                            : "Run IRL match"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Find Documents: cost-estimate confirmation ─────────── */}
                {showFindDocsConfirm && (
                  <div
                    style={{
                      marginTop: 8,
                      background: "#f4fbf1",
                      border: "1px solid #2f6b2f",
                      borderRadius: 6,
                      padding: "10px 12px",
                      fontSize: 12,
                      color: "#1f2937",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#2f6b2f", marginBottom: 4 }}>
                      Run folder match?
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>{formatCostEstimate(folderMatchCost)}</strong> estimated
                      &nbsp;·&nbsp; {folderMatchCost.folderCount} folder{folderMatchCost.folderCount === 1 ? "" : "s"}
                      &nbsp;·&nbsp; ~{folderMatchCost.inputTokens.toLocaleString()} input + ~{folderMatchCost.outputTokens.toLocaleString()} output tokens
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                      Reads the folders you've selected for matching in the IRL tab. Adjust folder selection there if you want to narrow / broaden the scope before running.
                    </div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        onClick={() => setShowFindDocsConfirm(false)}
                        style={{
                          background: "#fff",
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleFindDocuments()}
                        style={{
                          background: "#2f6b2f",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Run folder match
                      </button>
                    </div>
                  </div>
                )}
                {folderMatchError && (
                  <div style={{ fontSize: 11, color: "#dc2626", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 8px", marginTop: 8 }}>
                    ❌ {folderMatchError}
                  </div>
                )}

                {/* ── Folder Hints panel — surfaces folders that look like ── */}
                {/*    they satisfy specific IRL items based on name + samples */}
                {folderMatchHitCount > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      background: "#fff",
                      border: "1px solid #cfe6c7",
                      borderRadius: 6,
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#2f6b2f", marginBottom: 6 }}>
                      Folder hints ({folderMatchHitCount}) — review these folders against the matched IRL items
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflow: "auto" }}>
                      {folderCatalog
                        .filter((f) => (folderMatchResults[f.code]?.length ?? 0) > 0)
                        .map((f) => {
                          const top = folderMatchResults[f.code][0];
                          const irlMeta = irlListMap.get(top.number);
                          return (
                            <div
                              key={f.code}
                              style={{
                                fontSize: 11,
                                background: "#f4fbf1",
                                border: "1px solid #cfe6c7",
                                borderRadius: 6,
                                padding: "6px 8px",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                <span style={{ fontWeight: 600 }}>📁 {f.name}</span>
                                <span
                                  style={{
                                    background:
                                      top.confidence === "high"
                                        ? "#15803d"
                                        : top.confidence === "medium"
                                          ? "#ca8a04"
                                          : "#6b7280",
                                    color: "#fff",
                                    borderRadius: 4,
                                    padding: "1px 6px",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {top.confidence}
                                </span>
                                <span style={{ color: "#2f6b2f", fontWeight: 600 }}>
                                  → IRL {top.number}
                                </span>
                                {irlMeta && (
                                  <span style={{ color: "#4a7d4a" }}>: {irlMeta.request}</span>
                                )}
                              </div>
                              <div style={{ color: "#6b7280", marginLeft: 18 }}>
                                Sample files: {f.sampleFiles.slice(0, 3).join(", ")}
                                {f.sampleFiles.length > 3 ? ` … (+${f.sampleFiles.length - 3} more)` : ""}
                              </div>
                              {top.reasoning && (
                                <div style={{ color: "#6b7280", marginLeft: 18, fontStyle: "italic" }}>
                                  {top.reasoning}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                {projectId && !irl.hasIrlList && (
                  <div style={{ fontSize: 11, color: "#92651e", background: "#fffbeb", border: "1px solid #f5d97e", borderRadius: 6, padding: "6px 8px", marginTop: 8 }}>
                    ⓘ No IRL list uploaded for this project — matching is disabled. Upload one in the IRL tab to enable.
                  </div>
                )}
                {irl.error && (
                  <div style={{ fontSize: 11, color: "#dc2626", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 8px", marginTop: 8 }}>
                    ❌ {irl.error}
                  </div>
                )}

                <div style={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#4a7d4a",
                  marginTop: 8,
                  maxHeight: 220,
                  overflow: "auto",
                  background: "#fafdf8",
                  padding: 8,
                  borderRadius: 6,
                }}>
                  {diff.newFiles.slice(0, 200).map((f, i) => {
                    const folderCode = irlFolderCodes[i];
                    const folderExcluded = isCodeExcluded(folderCode, excludedFoldersSet);
                    const match = irl.matches[f.file.name];
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          📄 {f.relativePath} <span style={{ color: "#6b7280" }}>({(f.file.size / 1024).toFixed(0)} KB)</span>
                          <CopyTag count={diff.copyCounts.get(f.fileName.toLowerCase())} />
                        </span>
                        {projectId && irl.hasIrlList && renderMirrorIrlBadge(match, folderExcluded, irl.classifying, irlListMap)}
                      </div>
                    );
                  })}
                  {diff.newFiles.length > 200 && (
                    <div style={{ color: "#6b7280", marginTop: 4 }}>… and {diff.newFiles.length - 200} more</div>
                  )}
                </div>
              </details>
            )}

            {diff.skippedFiles.length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, color: "#6b7280" }}>
                  Already synced ({diff.skippedFiles.length})
                </summary>
                <div style={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#6b7280",
                  marginTop: 8,
                  maxHeight: 180,
                  overflow: "auto",
                  background: "#f8f9f7",
                  padding: 8,
                  borderRadius: 6,
                }}>
                  {diff.skippedFiles.slice(0, 100).map((f, i) => (
                    <div key={i}>☑ {f.relativePath}<CopyTag count={diff.copyCounts.get(f.fileName.toLowerCase())} /></div>
                  ))}
                  {diff.skippedFiles.length > 100 && <div>… and {diff.skippedFiles.length - 100} more</div>}
                </div>
              </details>
            )}

            <button
              onClick={runUpload}
              disabled={uploading || nothingToDo}
              style={{
                background: uploading || nothingToDo ? "#9bbf8f" : "#15803d",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "12px 16px",
                cursor: uploading || nothingToDo ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 14,
                width: "100%",
              }}
            >
              {uploading
                ? "Uploading..."
                : nothingToDo
                ? "✅ Nothing to upload"
                : diff.newFiles.length === 0
                ? `3️⃣ Check ${multiFolderFiles.toCheck} multi-folder cop${multiFolderFiles.toCheck !== 1 ? "ies" : "y"} & upload any missing`
                : `3️⃣ Create ${diff.newFolders.length} folder(s) & upload ${diff.newFiles.length} file(s)`}
            </button>
          </div>
        </div>
      )}

      {/* Upload progress table */}
      {statuses.length > 0 && (
        <div
          style={{
            marginTop: 16,
            border: "1px solid #c1e2b3",
            borderRadius: 10,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#eaf7e6",
              padding: "12px 16px",
              borderBottom: "1px solid #c1e2b3",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontWeight: 700, color: "#2f6b2f" }}>
              Upload Progress ({statuses.filter(s => s.status === "done").length}/{statuses.length})
            </div>
          </div>
          <div style={{ maxHeight: 300, overflow: "auto" }}>
            {statuses.map((s, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 16px",
                  borderBottom: "1px solid #e5f2e0",
                  fontSize: 12,
                  fontFamily: "monospace",
                  color:
                    s.status === "done"
                      ? "#166534"
                      : s.status === "error"
                      ? "#dc2626"
                      : s.status === "uploading"
                      ? "#3b82f6"
                      : "#6b7280",
                }}
              >
                <span>
                  {s.status === "done" ? "✓" : s.status === "error" ? "✗" : s.status === "uploading" ? "↑" : "•"}{" "}
                  {s.folderPath ? `${s.folderPath}/` : ""}
                  <b>{s.fileName}</b>
                </span>
                <span>
                  {s.status === "uploading" && s.progress !== undefined
                    ? `${s.progress}%`
                    : s.status === "error"
                    ? s.error
                    : s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ fontSize: 12, color: "#2f6b2f", marginBottom: 4 }}>
              {m}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function renderMirrorIrlBadge(
  match: import("../irl/match/types").MatchResult | undefined,
  folderExcluded: boolean,
  classifying: boolean,
  irlListMap: Map<string, { topic?: string; request: string }>,
): React.ReactNode {
  if (folderExcluded) {
    return (
      <span
        title="This file's folder is excluded in the IRL tab."
        style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic", whiteSpace: "nowrap" }}
      >
        — folder excluded
      </span>
    );
  }
  if (!match) {
    return classifying ? (
      <span style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic", whiteSpace: "nowrap" }}>matching…</span>
    ) : (
      <span style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>—</span>
    );
  }
  if (match.irlNumber) {
    const meta = irlListMap.get(match.irlNumber);
    const topicLine = meta?.topic ? `Topic: ${meta.topic}\n` : "";
    const tooltip = `IRL ${match.irlNumber}\n${topicLine}${meta?.request ?? ""}\n\n${match.reasoning}\n\nConfidence: ${(match.confidence * 100).toFixed(0)}%`;
    return (
      <span
        title={tooltip}
        style={{
          display: "inline-block",
          padding: "1px 6px",
          borderRadius: 5,
          background: "#dff3d9",
          color: "#15803d",
          fontWeight: 600,
          fontSize: 10,
          fontFamily: "monospace",
          cursor: "help",
          whiteSpace: "nowrap",
        }}
      >
        IRL {match.irlNumber}
      </span>
    );
  }
  if (match.alternatives.length > 0) {
    const altsLine = match.alternatives
      .map((a) => `${a.number} (${(a.confidence * 100).toFixed(0)}%)`)
      .join(", ");
    return (
      <span
        title={`Low confidence — top candidates: ${altsLine}\n\n${match.reasoning}`}
        style={{ fontSize: 10, color: "#b7950b", fontStyle: "italic", cursor: "help", whiteSpace: "nowrap" }}
      >
        — low conf.
      </span>
    );
  }
  return (
    <span title={match.reasoning} style={{ fontSize: 10, color: "#9ca3af", cursor: "help", whiteSpace: "nowrap" }}>
      —
    </span>
  );
}

function CopyTag({ count }: { count?: number }) {
  if (!count || count < 2) return null;
  return <span style={{ color: "#92400e" }}> · in {count} folders</span>;
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        background: "#fff",
        padding: "12px 16px",
        borderRadius: 10,
        minWidth: 130,
        border: `1px solid ${color}33`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
